import { memo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowRightFromBracket';
import { Trans, useTranslation } from 'react-i18next';
import { useTargetBrandConfig } from 'FrontRoyalAngular';
import { Button } from 'Button';
import { MailtoLink } from 'MailtoLink';
import { type LearnerProject } from 'LearnerProjects';
import { twMerge } from 'tailwind-merge';
import { Text } from 'Text';
import './locales/view_project_group_modal-en.json';
import { StatusCapsule } from './StatusCapsule';
import { getMemberDetailsFromProjectGroupMembership } from './getMemberDetails';
import { LeaveProjectGroupConfirmationModal } from './LeaveProjectGroupConfirmationModal';
import { MembersList } from './MembersList';
import { projectGroupsApi } from './projectGroupsApi';
import {
    type MemberDetails,
    type ProjectGroupMembershipSansProjectGroup,
    MemberDetailsMembershipStatus,
    ProjectGroupMembershipStatus,
} from './ProjectGroup.types';

const Description = ({ projectTitle }: { projectTitle: string }) => {
    const { t } = useTranslation('back_royal');
    return (
        <Text color="black">
            <Trans
                i18nKey="view_project_group_modal_page.description"
                t={t}
                components={{
                    strong: <span className="font-semibold" />,
                }}
                values={{ projectTitle }}
            />
        </Text>
    );
};

const StatusSummary = ({
    className,
    memberships,
}: {
    className?: string;
    memberships: ProjectGroupMembershipSansProjectGroup[];
}) => {
    const statusSummaryMap = {
        accepted: memberships.filter(membership => membership.status === ProjectGroupMembershipStatus.ACCEPTED).length,
        invited: memberships.filter(membership => membership.status === ProjectGroupMembershipStatus.INVITED).length,
        declined: memberships.filter(membership =>
            [ProjectGroupMembershipStatus.DECLINED, ProjectGroupMembershipStatus.REMOVED].includes(
                // This modal shows users that have a membership to this group, so they must have a membership status.
                membership.status!,
            ),
        ).length,
    } as Record<ProjectGroupMembershipStatus, number>;
    const statusCapsules = Object.entries(statusSummaryMap)
        .map(([status, count]) => {
            if (count === 0) return null;

            return <StatusCapsule key={status} status={status as MemberDetailsMembershipStatus} count={count} />;
        })
        .filter(Boolean);
    return <div className={twMerge('flex justify-center gap-2.5', className)}>{statusCapsules}</div>;
};

const ConfirmationButtons = ({
    className,
    projectGroupMembership,
    onCancel,
    onConfirm,
}: {
    className?: string;
    projectGroupMembership: ProjectGroupMembershipSansProjectGroup;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const { t } = useTranslation('back_royal');
    return (
        <div className={twMerge('flex flex-col justify-center gap-4', className)}>
            <Button color="black" onPress={onConfirm}>
                {t('view_project_group_modal_page.confirm_button_label')}
            </Button>
            {!projectGroupMembership.isCreator && (
                <Button
                    className="self-center text-center text-base font-normal text-black sm:text-base md:text-base"
                    variant="light"
                    height={8}
                    onPress={onCancel}
                    icon={<FontAwesomeIcon icon={faArrowRightFromBracket} />}
                >
                    {t('view_project_group_modal_page.cancel_button_label')}
                </Button>
            )}
        </div>
    );
};

const Footnote = ({ className }: { className?: string }) => {
    const { t } = useTranslation('back_royal');
    const brandConfig = useTargetBrandConfig();
    const projectEmailAddress = brandConfig.emailAddressForUsername('projects');
    return (
        <div className={twMerge('flex justify-center text-center', className)}>
            <Text as="small" color="slate-grey">
                {t('view_project_group_modal_page.footnote', {
                    email: <MailtoLink className="text-xs" email={projectEmailAddress} key="projects-mailto" />,
                })}
            </Text>
        </div>
    );
};

function ViewProjectGroupModalPageComponent(props: {
    closeModal: () => void;
    isOpen: boolean;
    project: LearnerProject;
    projectGroupMembership: ProjectGroupMembershipSansProjectGroup;
    isLeaveGroupModalOpen: boolean;
    isLeavingGroup: boolean;
    apiError: string | null;
    onLeaveGroupModalClose: () => void;
    onLeaveGroupPress: () => void;
    onLeaveGroupConfirmPress: () => void;
}) {
    const {
        closeModal,
        isOpen,
        project,
        projectGroupMembership,
        isLeaveGroupModalOpen,
        isLeavingGroup,
        apiError,
        onLeaveGroupModalClose,
        onLeaveGroupPress,
        onLeaveGroupConfirmPress,
    } = props;
    const { data: projectGroupResponse } = projectGroupsApi.useGetProjectGroupQuery(
        projectGroupMembership.projectGroupId,
    );
    const projectGroup = projectGroupResponse?.contents.projectGroups[0];
    if (!isOpen) return null;
    if (!projectGroup) return null;

    // Sort order is as follows:
    // 1. Creator
    // 2. Current user (accepted)
    // 3. Other accepted members
    // 4. Invited members
    // 5. Declined/removed members
    const members = projectGroup.memberships!.map(getMemberDetailsFromProjectGroupMembership).sort((a, b) => {
        if (a.detailsStatus === MemberDetailsMembershipStatus.CREATOR) return -1;
        if (b.detailsStatus === MemberDetailsMembershipStatus.CREATOR) return 1;
        if (a.id === projectGroupMembership.userId) return -1;
        if (b.id === projectGroupMembership.userId) return 1;
        if (a.detailsStatus === MemberDetailsMembershipStatus.ACCEPTED) return -1;
        if (b.detailsStatus === MemberDetailsMembershipStatus.ACCEPTED) return 1;
        if (a.detailsStatus === MemberDetailsMembershipStatus.INVITED) return -1;
        if (b.detailsStatus === MemberDetailsMembershipStatus.INVITED) return 1;
        if (a.detailsStatus === MemberDetailsMembershipStatus.DECLINED) return -1;
        if (b.detailsStatus === MemberDetailsMembershipStatus.DECLINED) return 1;
        return 0;
    });

    const showMemberStatus = (member: MemberDetails) =>
        member.detailsStatus === MemberDetailsMembershipStatus.CREATOR ||
        member.detailsStatus !== MemberDetailsMembershipStatus.ACCEPTED;

    const membersListItemBaseClassName = (member: MemberDetails) =>
        member.detailsStatus === MemberDetailsMembershipStatus.DECLINED ? 'opacity-70' : '';

    const membersListItemAvatarClassName = (member: MemberDetails) =>
        member.detailsStatus === MemberDetailsMembershipStatus.DECLINED ? 'grayscale' : '';

    return (
        <>
            <Description projectTitle={project.title} />
            <StatusSummary className="my-7.5" memberships={projectGroup.memberships!} />
            <MembersList
                className={{
                    memberslistItem: { base: membersListItemBaseClassName, avatar: membersListItemAvatarClassName },
                }}
                members={members}
                showMemberStatus={showMemberStatus}
            />
            <ConfirmationButtons
                className="mt-12.5"
                projectGroupMembership={projectGroupMembership}
                onCancel={onLeaveGroupPress}
                onConfirm={closeModal}
            />
            <Footnote className="mt-3.5" />
            <LeaveProjectGroupConfirmationModal
                isOpen={isLeaveGroupModalOpen}
                onClose={onLeaveGroupModalClose}
                onConfirm={onLeaveGroupConfirmPress}
                isLoading={isLeavingGroup}
                apiError={apiError}
            />
        </>
    );
}
export const ViewProjectGroupModalPage = memo(ViewProjectGroupModalPageComponent);
